import { v4 as uuidv4 } from 'uuid';
import { normalizeStoredImageItem } from './localImage.js';
import { LOCAL_ASSET } from './localAssets.js';
import { getQiniuSignedUrl } from './qiniuMedia.js';

export const PURE_IMAGE_H5_SCREEN_WIDTH = 375;
export const PURE_IMAGE_H5_SIDE_INSET = 12;
export const PURE_IMAGE_H5_CONTENT_WIDTH = PURE_IMAGE_H5_SCREEN_WIDTH - PURE_IMAGE_H5_SIDE_INSET * 2;
export const PURE_IMAGE_PC_SIDE_INSET = 8;
export const PURE_IMAGE_COL_GAP = 12;

export function createPureImageGridUtils(options) {
	const {
		type,
		colCount,
		defaultHeight,
		defaultBorderRadius = 0,
		defaultItemCount,
		imageIdPrefix,
		randomStart = 80,
		colGap = PURE_IMAGE_COL_GAP,
		assetKey = 'imgQuad',
	} = options;

	function createDefaultItem(preset = {}, index = 0) {
		const localImgs = preset?.images || LOCAL_ASSET[assetKey] || LOCAL_ASSET.imgQuad;
		return {
			id: uuidv4(),
			image: {
				id: `${imageIdPrefix}-${uuidv4()}`,
				filename: preset.filename || 'demo.jpg',
				previewUrl: localImgs[index % localImgs.length],
				url: '',
				local: false,
			},
		};
	}

	function createDefaultItems(count = defaultItemCount) {
		const items = [];
		for (let i = 0; i < count; i += 1) {
			items.push(createDefaultItem({}, i));
		}
		return items;
	}

	function getDefaults(extra = {}) {
		return normalizeData({
			items: createDefaultItems(defaultItemCount),
			fullWidth: true,
			...extra,
		});
	}

	function isBlock(blockType) {
		return blockType === type;
	}

	function getImageUrl(image) {
		if (!image) return '';
		return getQiniuSignedUrl(image.previewUrl || image.url || '');
	}

	function normalizeImage(image, fallback) {
		if (!image?.previewUrl && !image?.url) {
			return fallback ? { ...fallback } : null;
		}
		return normalizeStoredImageItem(image, fallback);
	}

	function normalizeItem(item = {}, index = 0) {
		const fallback = createDefaultItem({}, index);
		return {
			id: item.id || uuidv4(),
			image: normalizeImage(item.image, fallback.image),
		};
	}

	function normalizeItems(items = []) {
		if (!items.length) {
			return createDefaultItems(defaultItemCount);
		}
		return items.map((item, index) => normalizeItem(item, index));
	}

	function normalizeData(data = {}) {
		const height = Number(data.imageHeight);
		return {
			items: normalizeItems(data.items),
			imageHeight: Number.isFinite(height) && height > 0
				? height
				: defaultHeight,
			borderRadius: Number.isFinite(Number(data.borderRadius))
				? Math.max(0, Number(data.borderRadius))
				: defaultBorderRadius,
			fullWidth: data.fullWidth !== false,
		};
	}

	function getH5ContentWidth(fullWidth = false) {
		return fullWidth ? PURE_IMAGE_H5_SCREEN_WIDTH : PURE_IMAGE_H5_CONTENT_WIDTH;
	}

	function getRefColumnWidth(contentWidth, fullWidth = false) {
		const width = contentWidth ?? getH5ContentWidth(fullWidth);
		const gaps = colGap * (colCount - 1);
		return (width - gaps) / colCount;
	}

	function getThumbAspectRatio(imageHeight = defaultHeight, fullWidth = false) {
		const columnWidth = getRefColumnWidth(undefined, fullWidth);
		const height = Number(imageHeight) || defaultHeight;
		return `${columnWidth} / ${height}`;
	}

	function getBlockStyle(data = {}, { h5 = false } = {}) {
		const normalized = normalizeData(data);
		const pxRatio = h5 ? 50 : 1;
		const radius = normalized.borderRadius;
		const ratioFullWidth = h5 ? normalized.fullWidth : true;

		return {
			fullWidth: normalized.fullWidth,
			aspectRatio: getThumbAspectRatio(normalized.imageHeight, ratioFullWidth),
			borderRadius: h5 ? `${radius / pxRatio}rem` : `${radius}px`,
			pcSideInset: `${PURE_IMAGE_PC_SIDE_INSET}px`,
			h5SideInset: `${PURE_IMAGE_H5_SIDE_INSET / pxRatio}rem`,
			colGap: h5 ? `${colGap / pxRatio}rem` : `${colGap}px`,
		};
	}

	function getGalleryUrls(data = {}) {
		return normalizeData(data).items
			.map(item => getImageUrl(item.image))
			.filter(Boolean);
	}

	return {
		TYPE: type,
		COL_COUNT: colCount,
		COL_GAP: colGap,
		DEFAULT_HEIGHT: defaultHeight,
		BATCH_SIZE: colCount,
		createDefaultItem,
		createDefaultItems,
		getDefaults,
		isBlock,
		getImageUrl,
		normalizeData,
		getBlockStyle,
		getGalleryUrls,
		getThumbAspectRatio,
	};
}
